import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, ToggleInput } from 'components/Shared';
import { FORM_BACK_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchOperationTypeActions as FetchActions,
  UpdateOperationTypeActions as UpdateActions,
} from 'store/ducks/operationTypes';
import { UpdateOperationTypeValidator } from 'validators/OperationTypes';
import * as S from './styles';

interface Props {
  typeId: string | number;
  onUpdate?: () => void;
}

export const TypeUpdateForm: React.FC<Props> = ({ typeId, onUpdate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();

  const { data: operationType } = useSelector(
    (state: RootState) => state.fetchOperationType
  );

  const { loading: updatingOperationType, validationErrors } = useSelector(
    (state: RootState) => state.updateOperationType
  );

  const fetchOperationType = useCallback(() => {
    dispatch(FetchActions.request(typeId));
  }, [dispatch, typeId]);

  const onOperationTypeLoad = useCallback((): void => {
    if (!operationType) return;
    const { name, blockedAt } = operationType;

    formRef.current?.setData({
      name,
      blockedAt: blockedAt ? '1' : '0',
    });
  }, [operationType]);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});

        const { schema } = new UpdateOperationTypeValidator();

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(typeId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess, typeId]
  );

  useEffect(() => {
    fetchOperationType();
  }, [fetchOperationType]);

  useEffect(() => {
    onOperationTypeLoad();
  }, [onOperationTypeLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Input name="name" label="Nome" readOnly disabled />
          <ToggleInput name="blockedAt" label="Bloqueado" />
        </S.FormRow>
        <S.FormActions>
          <S.LinkButton mood="light" to="/configuracoes/tipos-operacoes">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit">
            {updatingOperationType ? <S.ActivityIndicator /> : 'Salvar'}
          </S.Button>
        </S.FormActions>
      </Form>
    </S.Container>
  );
};
